import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@apps/web/lib/utils';

/*
 * Underline tab strip for admin page headers. Purely presentational —
 * the page owns which panel renders for the active value (admin pages
 * keep the active tab in the URL search params). Built on the Radix
 * tabs primitives so the tablist keeps roving focus and arrow-key
 * navigation identical to the customer tabs it restyles.
 *
 * Round 4 polish: the active-tab underline is a single absolutely-
 * positioned indicator anchored at (0, 0) inside the tablist. It
 * reads the active trigger's bounding rect via useLayoutEffect and a
 * per-trigger ResizeObserver, then transitions `transform` and
 * `width` (never `left`) so the GPU can composite the slide without
 * per-frame layout. Inactive triggers render a 40%-opacity purple
 * placeholder on hover so the underline hint lands at the correct
 * edge without shifting the row height.
 */

export type AdminTabItem = {
    readonly value: string;
    readonly label: ReactNode;
};

type IndicatorRect = { readonly left: number; readonly width: number };

export function AdminTabs({
    items,
    value,
    onValueChange,
    className,
    ...props
}: Omit<
    React.ComponentProps<typeof TabsPrimitive.List>,
    'onChange' | 'defaultValue' | 'dir'
> & {
    readonly items: ReadonlyArray<AdminTabItem>;
    readonly value: string;
    readonly onValueChange: (value: string) => void;
}) {
    const listRef = useRef<HTMLDivElement>(null);
    const triggerRefs = useRef(new Map<string, HTMLButtonElement>());
    const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

    useLayoutEffect(() => {
        const list = listRef.current;
        const active = triggerRefs.current.get(value);
        if (!list || !active) {
            return;
        }

        // Compute indicator geometry relative to the tablist wrapper so
        // it stays correct regardless of parent scroll/transform.
        const measure = () => {
            const listRect = list.getBoundingClientRect();
            const activeRect = active.getBoundingClientRect();
            setIndicator({
                left: activeRect.left - listRect.left,
                width: activeRect.width,
            });
        };
        measure();

        // Re-measure when the active trigger or the tablist itself
        // resizes (font load, container width change).
        const observer = new ResizeObserver(measure);
        observer.observe(active);
        observer.observe(list);
        return () => {
            observer.disconnect();
        };
    }, [value, items]);

    return (
        // asChild collapses Root into the wrapper so the tablist keeps
        // its accessibility semantics while we own the positioning
        // context for the sliding-underline indicator.
        <TabsPrimitive.Root
            asChild={true}
            value={value}
            onValueChange={onValueChange}
        >
            <TabsPrimitive.List
                ref={listRef}
                data-slot="admin-tabs"
                className={cn(
                    'border-admin-border relative flex items-end gap-4 border-b',
                    className,
                )}
                {...props}
            >
                {items.map((item) => {
                    const isActive = item.value === value;
                    return (
                        <TabsPrimitive.Trigger
                            key={item.value}
                            ref={(node) => {
                                if (node) {
                                    triggerRefs.current.set(item.value, node);
                                } else {
                                    triggerRefs.current.delete(item.value);
                                }
                            }}
                            value={item.value}
                            data-active={isActive}
                            className={cn(
                                // Group scope so inactive triggers can
                                // reveal a hover-underline placeholder
                                // via ::after without extra markup.
                                // -mb-px overlaps the tablist's
                                // hairline base border. The `.admin-tab`
                                // hook wires up the theme-layer hover
                                // placeholder for inactive triggers.
                                'admin-tab text-admin-sm focus-visible:ring-admin-border-focus group relative -mb-px cursor-pointer px-0.5 pt-1 pb-2 transition-[color] duration-[var(--admin-dur-fast)] ease-[var(--admin-ease-in-out)] focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset',
                                isActive
                                    ? 'text-admin-ink font-medium'
                                    : 'text-admin-ink-muted hover:text-admin-ink-secondary',
                            )}
                        >
                            {item.label}
                        </TabsPrimitive.Trigger>
                    );
                })}
                {/*
                 * Sliding underline. Anchored at (0, 0) and driven by
                 * `translateX` + `width` so the browser composites the
                 * slide on the GPU rather than laying out on every
                 * frame. aria-hidden + no interactive props so it
                 * never steals focus from the tablist. Height 1.5px
                 * (2px minus the -mb-px overlap on the trigger) sits
                 * flush with the tablist's hairline base.
                 */}
                <span
                    data-testid="admin-tab-underline"
                    aria-hidden={true}
                    className="bg-admin-purple pointer-events-none absolute -bottom-px left-0 h-[1.5px] transition-[transform,width] duration-[var(--admin-dur-base)] ease-[var(--admin-ease-in-out)] will-change-transform"
                    style={{
                        transform: `translateX(${indicator?.left ?? 0}px)`,
                        width: indicator?.width ?? 0,
                        opacity: indicator ? 1 : 0,
                    }}
                />
            </TabsPrimitive.List>
        </TabsPrimitive.Root>
    );
}
